import { createAuthAxios, handleApiError } from "../../utils/apiUtils";
import { ROOMS_ENDPOINTS } from "../../config/apiConfig";

/**
 * Research Room Task Service
 * Handles all REST API calls for room tasks, Kanban board, subtasks, and task management
 */
class RoomTaskService {
  /**
   * Create a new task (Leader only)
   * @param {Object} taskData - Task creation data
   * @returns {Promise<Object>} Created task
   */
  async createTask(taskData) {
    try {
      if (!taskData.title || !taskData.description) {
        throw new Error("Title and description are required");
      }
      if (!taskData.assignedTo) {
        throw new Error("Task must be assigned to a user");
      }
      if (!taskData.researchRoomId) {
        throw new Error("Research room ID is required");
      }

      // 🔍 DEBUG: Log everything before request
      const user = JSON.parse(localStorage.getItem("user"));
const authAxios = createAuthAxios();
const response = await authAxios.post(
        ROOMS_ENDPOINTS.TASKS.CREATE,
        taskData
      );
return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  }

  /**
   * Get all tasks for a room
   * @param {String} roomId - Room ID
   * @returns {Promise<Object>} List of tasks
   */
  async getRoomTasks(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(ROOMS_ENDPOINTS.TASKS.LIST(roomId));
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get current user's tasks in a room
   * @param {String} roomId - Room ID
   * @returns {Promise<Object>} List of user's tasks
   */
  async getMyTasks(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.TASKS.MY_TASKS(roomId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get Kanban board for a room
   * @param {String} roomId - Room ID
   * @returns {Promise<Object>} Kanban board data organized by status
   */
  async getKanbanBoard(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.TASKS.KANBAN(roomId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get task statistics for a room
   * @param {String} roomId - Room ID
   * @returns {Promise<Object>} Task statistics
   */
  async getTaskStats(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(ROOMS_ENDPOINTS.TASKS.STATS(roomId));
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get a single task by ID
   * @param {String} taskId - Task ID
   * @returns {Promise<Object>} Task details
   */
  async getTaskById(taskId) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.TASKS.DETAIL(taskId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update a task (Leader only)
   * @param {String} taskId - Task ID
   * @param {Object} updates - Task updates
   * @returns {Promise<Object>} Updated task
   */
  async updateTask(taskId, updates) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        ROOMS_ENDPOINTS.TASKS.UPDATE(taskId),
        updates
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update task status (Leader only)
   * @param {String} taskId - Task ID
   * @param {String} status - New status
   * @returns {Promise<Object>} Updated task
   */
  async updateTaskStatus(taskId, status) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }
      if (!status) {
        throw new Error("Status is required");
      }

      const validStatuses = [
        "todo",
        "in-progress",
        "review",
        "completed",
        "blocked",
      ];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.patch(
        ROOMS_ENDPOINTS.TASKS.UPDATE_STATUS(taskId),
        { status }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete a task (Leader only)
   * @param {String} taskId - Task ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteTask(taskId) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        ROOMS_ENDPOINTS.TASKS.DELETE(taskId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Toggle subtask completion
   * @param {String} taskId - Task ID
   * @param {String} subtaskId - Subtask ID
   * @returns {Promise<Object>} Updated subtask and progress
   */
  async toggleSubtask(taskId, subtaskId) {
    try {
      if (!taskId || !subtaskId) {
        throw new Error("Task ID and Subtask ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        ROOMS_ENDPOINTS.TASKS.TOGGLE_SUBTASK(taskId, subtaskId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Add a comment to a task
   * @param {String} taskId - Task ID
   * @param {String} content - Comment content
   * @returns {Promise<Object>} Updated comments list
   */
  async addComment(taskId, content) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }
      if (!content || !content.trim()) {
        throw new Error("Comment content is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        ROOMS_ENDPOINTS.TASKS.ADD_COMMENT(taskId),
        { content: content.trim() }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Attach a file to a task
   * @param {String} taskId - Task ID
   * @param {String} attachmentId - Attachment ID from room files
   * @returns {Promise<Object>} Updated attachments list
   */
  async attachFile(taskId, attachmentId) {
    try {
      if (!taskId) {
        throw new Error("Task ID is required");
      }
      if (!attachmentId) {
        throw new Error("Attachment ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        ROOMS_ENDPOINTS.TASKS.ATTACH_FILE(taskId),
        { attachmentId }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get overdue tasks for a room
   * @param {String} roomId - Room ID
   * @returns {Promise<Object>} List of overdue tasks
   */
  async getOverdueTasks(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.TASKS.OVERDUE(roomId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default new RoomTaskService();
